import { useState } from "preact/hooks";
import type { JSX } from "preact";
import {
  confirmDialog,
  createFolder,
  deleteFolder,
  emptyFolder,
  folderLabel,
  logout,
  markFolderRead,
  moveMessages,
  openCompose,
  renameFolder,
  selectMailbox,
} from "../actions";
import {
  currentMailbox,
  drawerOpen,
  folderActionError,
  folderCreateParent,
  folderEditPath,
  mailboxes,
  showCommandPalette,
  showSettings,
  showShortcuts,
  userEmail,
  userSettings,
} from "../state";
import { prefs, setPref, type ThemePref } from "../prefs";
import { t } from "../i18n";
import type { Mailbox, SpecialUse } from "../../shared/types";
import { Icon, type IconName } from "./icons";
import { Logo } from "./Logo";
import { Avatar, Dropdown, MenuItem, Segmented } from "./kit";

const SPECIAL_ICON: Partial<Record<NonNullable<SpecialUse>, IconName>> = {
  inbox: "inbox",
  drafts: "drafts",
  sent: "sent",
  archive: "archive",
  junk: "spam",
  trash: "trash",
  flagged: "star",
  all: "mail",
};

const SYSTEM_ORDER: NonNullable<SpecialUse>[] = ["inbox", "flagged", "drafts", "sent", "archive", "junk", "trash", "all"];

function iconFor(m: Mailbox): IconName {
  return (m.specialUse && SPECIAL_ICON[m.specialUse]) || "folder";
}

/** Replace the last path segment with a new name. */
function replaceLastSegment(path: string, delimiter: string, newName: string): string {
  const idx = path.lastIndexOf(delimiter);
  if (idx < 0) return newName;
  return path.slice(0, idx + delimiter.length) + newName;
}

/** Extract the display name (last segment) from an IMAP path. */
function lastSegment(path: string, delimiter: string): string {
  const idx = path.lastIndexOf(delimiter);
  return idx < 0 ? path : path.slice(idx + delimiter.length);
}

function parentOf(m: Mailbox): string | null {
  const idx = m.path.lastIndexOf(m.delimiter);
  return idx < 0 ? null : m.path.slice(0, idx);
}

/** Payload carried by dragged message rows. */
export interface DragPayload {
  mailbox: string;
  uids: number[];
}

export const DRAG_MIME = "application/x-twm-messages";

export function Sidebar({ collapsed }: { collapsed: boolean }): JSX.Element {
  const boxes = mailboxes.value;
  const system = SYSTEM_ORDER.map((u) => boxes.find((b) => b.specialUse === u)).filter((b): b is Mailbox => Boolean(b));
  const custom = boxes.filter((b) => b.specialUse === null);

  return (
    <aside
      class={`flex h-full shrink-0 flex-col border-r border-line bg-canvas transition-[width] ${collapsed ? "w-[60px]" : "w-[248px]"}`}
      aria-label={t("nav.label")}
    >
      <div class={`flex h-14 shrink-0 items-center gap-2 ${collapsed ? "justify-center" : "px-4"}`}>
        <button
          class="flex min-w-0 items-center gap-2.5 rounded-md"
          onClick={() => void selectMailbox("INBOX")}
          title={t("folder.inbox")}
        >
          <Logo size={26} />
          {!collapsed && <span class="text-[15px] font-semibold tracking-tight text-fg">MailTrack</span>}
        </button>
        {!collapsed && (
          <button
            class="icon-btn icon-btn-sm ml-auto hidden md:inline-flex"
            title={t("nav.collapse")}
            aria-label={t("nav.collapse")}
            onClick={() => setPref("sidebarCollapsed", true)}
          >
            <Icon name="panelLeft" size={16} />
          </button>
        )}
      </div>

      <div class={collapsed ? "px-2.5 pb-3" : "px-3 pb-3"}>
        <button
          class={`btn btn-primary w-full ${collapsed ? "!h-10 !px-0" : "!h-10 justify-start"}`}
          onClick={() => {
            drawerOpen.value = false;
            openCompose();
          }}
          title={t("compose.new")}
        >
          <Icon name="drafts" size={17} />
          {!collapsed && <span class="flex-1 text-left">{t("compose.new")}</span>}
          {!collapsed && <kbd class="hidden rounded bg-on-accent/15 px-1.5 text-[11px] font-medium md:inline">C</kbd>}
        </button>
      </div>

      <nav class={`min-h-0 flex-1 overflow-y-auto pb-3 ${collapsed ? "px-2.5" : "px-2"}`}>
        <ul class="space-y-px">
          {system.map((m) => (
            <NavItem key={m.path} m={m} collapsed={collapsed} depth={0} />
          ))}
        </ul>

        {!collapsed ? (
          <FolderTree custom={custom} />
        ) : (
          custom.length > 0 && (
            <ul class="mt-3 space-y-px border-t border-line pt-3">
              {custom.map((m) => (
                <NavItem key={m.path} m={m} collapsed depth={0} />
              ))}
            </ul>
          )
        )}
      </nav>

      <AccountMenu collapsed={collapsed} />
    </aside>
  );
}

function FolderTree({ custom }: { custom: Mailbox[] }): JSX.Element {
  const [open, setOpen] = useState(true);
  const [createInput, setCreateInput] = useState("");
  const [closedParents, setClosedParents] = useState<Set<string>>(new Set());
  const creating = folderCreateParent.value !== undefined;

  // Depth-first ordering so children render right under their parent.
  const paths = new Set(custom.map((m) => m.path));
  const roots = custom.filter((m) => !parentOf(m) || !paths.has(parentOf(m)!));
  const childrenOf = (p: string): Mailbox[] => custom.filter((m) => parentOf(m) === p);
  const rows: Array<{ m: Mailbox; depth: number; hasKids: boolean }> = [];
  const visit = (m: Mailbox, depth: number): void => {
    const kids = childrenOf(m.path);
    rows.push({ m, depth, hasKids: kids.length > 0 });
    if (!closedParents.has(m.path)) kids.forEach((k) => visit(k, depth + 1));
  };
  roots.forEach((r) => visit(r, 0));

  function toggleParent(p: string): void {
    const next = new Set(closedParents);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setClosedParents(next);
  }

  function confirmCreate(): void {
    const name = createInput.trim();
    if (!name) return;
    setCreateInput("");
    void createFolder(name, folderCreateParent.value ?? undefined);
  }

  const parentName = folderCreateParent.value ? lastSegment(folderCreateParent.value, "/") : null;

  return (
    <div class="mt-5">
      <div class="group flex items-center gap-1 pl-2.5 pr-1">
        <button
          onClick={() => setOpen(!open)}
          class="flex flex-1 items-center gap-1.5 py-1 text-xs font-medium text-fg-3 hover:text-fg"
          aria-expanded={open}
        >
          {t("nav.folders")}
          <Icon name={open ? "chevronDown" : "chevronRight"} size={13} class="opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
        <button
          title={t("nav.newFolder")}
          aria-label={t("nav.newFolder")}
          onClick={() => {
            folderCreateParent.value = null;
            setCreateInput("");
            setOpen(true);
          }}
          class="icon-btn h-6 w-6"
        >
          <Icon name="plus" size={15} />
        </button>
      </div>

      {folderActionError.value && <p class="px-2.5 py-1 text-xs text-danger">{folderActionError.value}</p>}

      {creating && open && (
        <div class="px-1.5 py-1">
          <input
            autoFocus
            type="text"
            placeholder={parentName ? t("nav.subfolderOf", { name: parentName }) : t("nav.folderName")}
            value={createInput}
            onInput={(e) => setCreateInput((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmCreate();
              else if (e.key === "Escape") {
                e.stopPropagation();
                folderCreateParent.value = undefined;
              }
            }}
            onBlur={() => {
              if (!createInput.trim()) folderCreateParent.value = undefined;
            }}
            class="field !h-8"
          />
        </div>
      )}

      {open && (
        <ul class="mt-0.5 space-y-px">
          {rows.map(({ m, depth, hasKids }) => (
            <NavItem
              key={m.path}
              m={m}
              depth={depth}
              collapsed={false}
              custom
              hasKids={hasKids}
              kidsOpen={!closedParents.has(m.path)}
              onToggleKids={() => toggleParent(m.path)}
            />
          ))}
          {rows.length === 0 && !creating && <li class="px-2.5 py-1.5 text-xs text-fg-4">{t("nav.noFolders")}</li>}
        </ul>
      )}
    </div>
  );
}

function NavItem({
  m,
  collapsed,
  depth,
  custom,
  hasKids,
  kidsOpen,
  onToggleKids,
}: {
  m: Mailbox;
  collapsed: boolean;
  depth: number;
  custom?: boolean;
  hasKids?: boolean;
  kidsOpen?: boolean;
  onToggleKids?: () => void;
}): JSX.Element {
  const active = currentMailbox.value === m.path;
  const [dragOver, setDragOver] = useState(false);
  const [renameInput, setRenameInput] = useState("");
  const isRenaming = custom && folderEditPath.value === m.path;
  const label = custom ? m.name : folderLabel(m.path);
  // Drafts shows how many exist; everything else how many are unread.
  const count = m.specialUse === "drafts" ? m.total : m.specialUse === "sent" || m.specialUse === "trash" ? 0 : m.unread;
  const emphasize = m.specialUse === "inbox" || custom;

  function confirmRename(): void {
    const name = renameInput.trim();
    if (!name || name === m.name) {
      folderEditPath.value = null;
      return;
    }
    void renameFolder(m.path, replaceLastSegment(m.path, m.delimiter || "/", name));
  }

  const dropHandlers = {
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes(DRAG_MIME)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOver(true);
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      try {
        const payload = JSON.parse(e.dataTransfer!.getData(DRAG_MIME)) as DragPayload;
        if (payload.mailbox !== m.path) {
          void moveMessages(payload.uids.map((uid) => ({ uid, mailbox: payload.mailbox })), m.path);
        }
      } catch {
        /* ignore malformed data */
      }
    },
  };

  if (isRenaming) {
    return (
      <li class="px-1.5 py-0.5" style={`padding-left:${6 + depth * 14}px`}>
        <input
          autoFocus
          type="text"
          value={renameInput}
          onInput={(e) => setRenameInput((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") confirmRename();
            else if (e.key === "Escape") {
              e.stopPropagation();
              folderEditPath.value = null;
            }
          }}
          onBlur={confirmRename}
          class="field !h-8"
        />
      </li>
    );
  }

  return (
    <li class="group relative" {...dropHandlers}>
      <button
        onClick={() => void selectMailbox(m.path)}
        title={collapsed ? `${label}${count ? ` (${count})` : ""}` : undefined}
        aria-current={active ? "page" : undefined}
        class={`flex h-9 w-full items-center gap-3 rounded-[7px] text-[13.5px] transition-colors ${
          collapsed ? "justify-center" : "pl-2.5 pr-2"
        } ${active ? "bg-surface font-medium text-fg shadow-[0_0_0_1px_var(--color-line)]" : "text-fg-2 hover:bg-raised"} ${
          dragOver ? "!bg-accent/12 ring-2 ring-accent" : ""
        }`}
        style={!collapsed && custom ? `padding-left:${26 + depth * 14}px` : undefined}
      >
        <span class="relative">
          <Icon name={iconFor(m)} size={17} class={active ? "text-accent-fg" : "text-fg-3"} />
          {collapsed && count > 0 && m.specialUse !== "drafts" && (
            <span class="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-accent ring-2 ring-canvas" />
          )}
        </span>
        {!collapsed && <span class="min-w-0 flex-1 truncate text-left">{label}</span>}
        {!collapsed && count > 0 && (
          <span
            class={`tabular-nums text-xs group-hover:hidden ${emphasize && m.specialUse !== "drafts" ? "font-semibold text-accent-fg" : "text-fg-3"}`}
          >
            {count > 999 ? "999+" : count}
          </span>
        )}
      </button>

      {!collapsed && hasKids && (
        <button
          class="icon-btn absolute top-1/2 h-5 w-5 -translate-y-1/2"
          style={`left:${4 + depth * 14}px`}
          aria-label={kidsOpen ? t("nav.collapseFolder") : t("nav.expandFolder")}
          onClick={(e) => {
            e.stopPropagation();
            onToggleKids?.();
          }}
        >
          <Icon name={kidsOpen ? "chevronDown" : "chevronRight"} size={12} />
        </button>
      )}

      {!collapsed && (
        <Dropdown
          class="absolute right-1 top-1/2 -translate-y-1/2"
          trigger={(p) => (
            <button
              {...p}
              title={t("nav.folderOptions")}
              aria-label={t("nav.folderOptions")}
              class="icon-btn h-7 w-7 opacity-0 focus:opacity-100 group-hover:opacity-100 aria-expanded:opacity-100"
            >
              <Icon name="dots" size={15} />
            </button>
          )}
        >
          {() => (
            <>
              <MenuItem icon="mailOpen" label={t("nav.markAllRead")} disabled={m.unread === 0} onSelect={() => void markFolderRead(m.path)} />
              {(m.specialUse === "trash" || m.specialUse === "junk") && (
                <MenuItem icon="trash" label={t("nav.emptyFolder")} danger disabled={m.total === 0} onSelect={() => void emptyFolder(m.path)} />
              )}
              {custom && (
                <>
                  <div class="menu-sep" />
                  <MenuItem
                    icon="drafts"
                    label={t("common.rename")}
                    onSelect={() => {
                      setRenameInput(lastSegment(m.path, m.delimiter || "/"));
                      folderEditPath.value = m.path;
                    }}
                  />
                  <MenuItem icon="folderPlus" label={t("nav.newSubfolder")} onSelect={() => (folderCreateParent.value = m.path)} />
                  <MenuItem
                    icon="trash"
                    label={t("nav.deleteFolder")}
                    danger
                    onSelect={() => void confirmDeleteFolder(m, Boolean(hasKids))}
                  />
                </>
              )}
            </>
          )}
        </Dropdown>
      )}
    </li>
  );
}

async function confirmDeleteFolder(m: Mailbox, hasKids: boolean): Promise<void> {
  const ok = await confirmDialog({
    title: t("confirm.deleteFolder.title", { name: m.name }),
    body: hasKids ? t("confirm.deleteFolder.kids") : m.total > 0 ? t("confirm.deleteFolder.notEmpty", { n: m.total }) : undefined,
    confirmLabel: t("nav.deleteFolder"),
    danger: true,
  });
  if (ok) void deleteFolder(m.path);
}

function AccountMenu({ collapsed }: { collapsed: boolean }): JSX.Element {
  const email = userEmail.value;
  const name = userSettings.value.displayName;
  return (
    <div class={`shrink-0 border-t border-line ${collapsed ? "p-2" : "p-2"}`}>
      {collapsed && (
        <button
          class="icon-btn mb-1 w-full"
          title={t("nav.expand")}
          aria-label={t("nav.expand")}
          onClick={() => setPref("sidebarCollapsed", false)}
        >
          <Icon name="panelLeft" size={17} />
        </button>
      )}
      <Dropdown
        up
        align="start"
        menuClass="w-[232px]"
        trigger={(p) => (
          <button
            {...p}
            class={`flex w-full items-center gap-2.5 rounded-[7px] p-1.5 text-left transition-colors hover:bg-raised ${collapsed ? "justify-center" : ""}`}
            title={email}
          >
            <Avatar address={email} label={name || email} size={30} />
            {!collapsed && (
              <span class="min-w-0 flex-1">
                <span class="block truncate text-[13px] font-medium text-fg">{name || email.split("@")[0]}</span>
                <span class="block truncate text-xs text-fg-3">{email}</span>
              </span>
            )}
            {!collapsed && <Icon name="chevronUp" size={14} class="text-fg-3" />}
          </button>
        )}
      >
        {() => (
          <>
            <div class="px-2.5 pb-2 pt-1.5">
              <div class="mb-1.5 text-xs text-fg-3">{t("settings.theme")}</div>
              <Segmented<ThemePref>
                size="sm"
                label={t("settings.theme")}
                value={prefs.value.theme}
                onChange={(v) => setPref("theme", v)}
                options={[
                  { value: "light", label: t("theme.light"), icon: "sun" },
                  { value: "dark", label: t("theme.dark"), icon: "moon" },
                  { value: "system", label: t("theme.auto"), icon: "monitor" },
                ]}
              />
            </div>
            <div class="menu-sep" />
            <MenuItem icon="settings" label={t("settings.title")} onSelect={() => (showSettings.value = true)} />
            <MenuItem icon="command" label={t("palette.title")} hint="Ctrl K" onSelect={() => (showCommandPalette.value = true)} />
            <MenuItem icon="keyboard" label={t("shortcuts.title")} hint="?" onSelect={() => (showShortcuts.value = true)} />
            <div class="menu-sep" />
            <MenuItem icon="logout" label={t("nav.signOut")} onSelect={() => void logout()} />
          </>
        )}
      </Dropdown>
    </div>
  );
}
